import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Drives the Catering screen. Loads all inquiries (capped at 200) and
// exposes group-by-status + upcoming-events derivations.
//
// Status workflow:
//   new       — manager hasn't acted yet
//   quoted    — quote sent to customer, waiting on response
//   confirmed — customer accepted; event will happen
//   completed — event happened
//   cancelled — customer cancelled
//   declined  — restaurant couldn't accommodate (no date capacity, etc.)
export function useCateringData() {
  const [inquiries, setInquiries] = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: qErr } = await supabase
        .from('catering_inquiries')
        .select('*')
        .order('event_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(200)
      if (qErr) throw qErr
      setInquiries(data ?? [])
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useCateringData]', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Same midnight-rollover + visibilitychange refresh pattern as
  // every other data hook in the project.
  useEffect(() => {
    let lastDate = new Date().toDateString()
    const id = setInterval(() => {
      const today = new Date().toDateString()
      if (today !== lastDate) {
        lastDate = today
        fetchAll()
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [fetchAll])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') fetchAll()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchAll])

  // Group inquiries by status — drives the inbox layout.
  const byStatus = useMemo(() => {
    const groups = {
      new:        [],
      quoted:     [],
      confirmed:  [],
      completed:  [],
      cancelled:  [],
      declined:   [],
    }
    for (const i of inquiries) {
      if (groups[i.status]) groups[i.status].push(i)
    }
    return groups
  }, [inquiries])

  // Upcoming events: confirmed or quoted, with event_date today or later.
  // Drives the "what's coming up" section at the top of the screen.
  const upcomingEvents = useMemo(() => {
    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    return inquiries
      .filter((i) => i.event_date && ['quoted', 'confirmed'].includes(i.status))
      .filter((i) => new Date(i.event_date) >= todayStart)
      .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
      .slice(0, 10)
  }, [inquiries])

  // ---------- Mutations -------------------------------------
  const createInquiry = useCallback(async (payload) => {
    // Never persist empty strings for nullable fields — coerce to null.
    const clean = (v) => (typeof v === 'string' && v.trim() === '' ? null : v)
    const insert = {
      contact_name:    payload.contact_name?.trim(),
      contact_email:   clean(payload.contact_email),
      contact_phone:   clean(payload.contact_phone),
      event_date:      clean(payload.event_date),
      event_time:      clean(payload.event_time),
      headcount:       payload.headcount ? Number(payload.headcount) : null,
      location:        clean(payload.location),
      event_type:      clean(payload.event_type),
      notes:           clean(payload.notes),
      estimated_total: payload.estimated_total ? Number(payload.estimated_total) : null,
      status:          payload.status || 'new',
    }
    // Multi-unit attribution: persist an explicit owning unit + assignment
    // when the caller provides them (a grouped operator with a unit
    // selected, or group-create's AI assignment). When absent (single-unit),
    // restaurant_id falls to the DB default trigger and assigned_restaurant_id
    // stays null — byte-identical to the pre-multi-unit behavior.
    if (payload.restaurant_id) insert.restaurant_id = payload.restaurant_id
    if (payload.assigned_restaurant_id) insert.assigned_restaurant_id = payload.assigned_restaurant_id
    const { data, error: insErr } = await supabase
      .from('catering_inquiries')
      .insert(insert)
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [fetchAll])

  const updateInquiry = useCallback(async (id, patch) => {
    const updates = { ...patch, updated_at: new Date().toISOString() }

    // M2: Only stamp *_at on the FORWARD transition. If we re-apply a
    // quote to an already-quoted inquiry, we don't want to clobber the
    // original quoted_at (follow-up message tone reads off that date).
    // If status flips back from 'confirmed' to 'quoted' (UI shouldn't
    // allow it, but defense in depth), don't silently demote the
    // existing confirmed_at by re-stamping quoted_at over a fresh now.
    const currentRow = inquiries.find((r) => r.id === id)
    const currentStatus = currentRow?.status
    const nowIso = new Date().toISOString()
    if (patch.status === 'quoted' && currentStatus !== 'quoted') {
      updates.quoted_at = updates.quoted_at ?? nowIso
    }
    if (patch.status === 'confirmed' && currentStatus !== 'confirmed') {
      updates.confirmed_at = updates.confirmed_at ?? nowIso
    }
    if (patch.status === 'completed' && currentStatus !== 'completed') {
      updates.completed_at = updates.completed_at ?? nowIso
    }

    // .select().single() so callers (e.g. CateringScreen.handleSave)
    // can resync their local copy of the row after a status transition
    // without waiting on fetchAll's round trip.
    const { data, error: updErr } = await supabase
      .from('catering_inquiries')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
    if (updErr) throw updErr
    await fetchAll()
    return data
  }, [fetchAll, inquiries])

  const deleteInquiry = useCallback(async (id) => {
    const { error: delErr } = await supabase
      .from('catering_inquiries')
      .delete()
      .eq('id', id)
    if (delErr) throw delErr
    await fetchAll()
  }, [fetchAll])

  return {
    inquiries,
    byStatus,
    upcomingEvents,
    loading,
    error,
    refresh: fetchAll,
    createInquiry,
    updateInquiry,
    deleteInquiry,
  }
}
